import React, { useEffect, useState, useCallback, useContext } from "react";

import { upperCaseFirst } from "change-case";
import classnames from "classnames";
import { get, isEmpty } from "lodash";
import PropTypes from "prop-types";
import {
  FormattedNumber,
  FormattedMessage,
  injectIntl,
  intlShape,
} from "react-intl";
import { Link } from "react-router";

// Submodules
import { Skeleton } from "@ps-design-system/components/Skeleton";

// Components
import { getListingMediaPriceAndInventory } from "../_v2/features/app-uploader/api/listingsApi";
import ListingStatus from "../_v2/features/pause-listing/components/ListingStatus";
import IntlProviderHOC from "../app-main/containers/IntlProviderHOC";
import { StatsigContext } from "../elements-statsig-provider/statsig-context";
import { Tooltip } from "../elements-tooltip/tooltip-v2";

// utilities

import { InstantBook } from "../shared-components/icons";

import { fsvMessages, tocByUseType } from "./data/utils";
import {
  getFlatRatePrice,
  getFoodAndDrinkMinimum,
  isFsvListing,
} from "./utils/listing-fsv.utils";

const ActiveListingsPanel = ({
  intl,
  panelClassname,
  modalId,
  toggleModal,
  workflow,
  ...listing
}) => {
  const [media, setMedia] = useState([]);
  const [listingDetails, setListingDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const {
    _id,
    use_type: useType = "",
    status = "",
    prices,
    is_instant_book_active = false,
    parentSpaceId = "",
  } = listing;

  const fetchListingDetails = useCallback(async (listingID) => {
    if (!listingID) {
      return {};
    }

    try {
      return await getListingMediaPriceAndInventory(listingID);
    } catch (err) {
      return {};
    }
  }, []);

  useEffect(() => {
    // LiveSpacePanel keys this card by space and use type, so it can switch
    // to another listing; ignore a request that finishes after that
    let isCurrent = true;

    const loadListingDetails = async () => {
      setLoading(true);
      try {
        const data = await fetchListingDetails(_id);
        if (!isCurrent) return;
        setMedia(data.embed_media || []);
        setListingDetails(data);
      } catch (error) {
        if (!isCurrent) return;
        setMedia([]);
        setListingDetails({});
      } finally {
        if (isCurrent) setLoading(false);
      }
    };
    if (_id) {
      loadListingDetails();
    } else {
      setLoading(false);
    }

    return () => {
      isCurrent = false;
    };
  }, [_id, fetchListingDetails]);

  const { featureGates } = useContext(StatsigContext);

  const isEnabled = featureGates.pause_listing ?? false;

  const isFsv = isFsvListing(
    isEmpty(listingDetails.full_service) ? listing : listingDetails,
  );
  const listingPrices = listingDetails.prices || prices;

  const currency = get(listingPrices, "0.currency", "USD");
  const price = get(prices, "0.value", 0);
  const useTypeHash = tocByUseType[useType];
  const listingTitle = isFsv ? fsvMessages.title : useTypeHash.title;
  const listingDescription = isFsv
    ? fsvMessages.description
    : useTypeHash.description;

  // Inventory is missing when the details request failed; hide the FSV
  // amounts then rather than showing a $0 minimum that was never loaded
  const inventory = get(listingDetails, "inventory.inventory");
  const hasInventory = Array.isArray(inventory);
  const foodAndDrinkMinimum = getFoodAndDrinkMinimum(inventory);
  const flatRatePrice = getFlatRatePrice(listingPrices);
  const spaceRental = Number(get(flatRatePrice, "value", 0)) || 0;
  const productionDetails = get(listing, "production_details", {});
  const eventDetails = get(listing, "event_details", {});

  const isMissingRequiredDetails = () => {
    if (useType === "Event") return isEmpty(eventDetails);
    if (useType === "Production") return isEmpty(productionDetails);
    return false;
  };

  const shouldSetupFaq =
    !loading && media.length > 0 && isMissingRequiredDetails();

  return (
    <IntlProviderHOC>
      <div className="active-panel listing-panel flex middle">
        <div className="panel-sections use-type-icon">
          <img
            alt={useTypeHash.icon}
            className="icon-xl"
            src={`/web-hosting/assets/images/${useTypeHash.icon}.svg?v=rb`}
          />
        </div>
        <div className="activated-use-type panel-sections flex-basis-70 flex-basis-50-sm">
          {loading ? (
            <Skeleton width={140} height={20} classNames="margin-sm-bottom" />
          ) : (
            <div>
              <div className="pull-left margin-sm-right heavier">
                {upperCaseFirst(intl.formatMessage(listingTitle))}
              </div>
              <div>
                <Tooltip
                  placement="top"
                  id={intl.formatMessage(listingTitle)}
                  anchorType="default"
                  hideOnClickAnywhere
                >
                  {intl.formatMessage(listingDescription)}
                </Tooltip>
              </div>
            </div>
          )}
          <div className="flex middle price-status-panel tw-gap-16">
            {/* status */}
            {isEnabled && <ListingStatus status={status} />}
            {/* price */}
            {loading && <Skeleton width={220} height={16} />}
            {!loading && isFsv && hasInventory && (
              <div className="text-gray-600">
                <span className="tw-block lg:tw-inline">
                  <FormattedNumber
                    value={foodAndDrinkMinimum}
                    currency={currency}
                    style="currency"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                  &nbsp;
                  <FormattedMessage
                    id="hosting.active_listings_panel.food_and_drink_minimum"
                    defaultMessage="food and drink minimum"
                  />
                </span>
                {/* Stacked below lg; one inline sentence with a bullet on desktop */}
                {spaceRental > 0 && (
                  <span className="tw-block lg:tw-inline">
                    <span className="tw-hidden lg:tw-inline">
                      &nbsp;&middot;&nbsp;
                    </span>
                    <FormattedNumber
                      value={spaceRental}
                      currency={currency}
                      style="currency"
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                    &nbsp;
                    <FormattedMessage
                      id="hosting.active_listings_panel.space_rental"
                      defaultMessage="space rental"
                    />
                  </span>
                )}
              </div>
            )}
            {!loading && !isFsv && (
              <div className="text-gray-600">
                <span>
                  <FormattedMessage
                    id="hosting.active_listings_panel.price_status_panel_from"
                    defaultMessage="From"
                  />
                  &nbsp;
                </span>
                <FormattedNumber
                  value={price}
                  currency={currency}
                  style="currency"
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
                /hr
              </div>
            )}

            {/* instant book */}
            {"ACTIVATED" === status && is_instant_book_active && (
              <div className="instant-book-setting flex middle status-panel">
                <InstantBook className="text-white margin-sm-right icon-xs" />
                <span className="text-gray-600">
                  <FormattedMessage
                    id="hosting.active_listings_panel.instant_book_text"
                    defaultMessage="Instant Book"
                  />
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex middle panel-sections panel-btn-col">
          {!loading && media.length === 0 && (
            <div className="margin-xl-right new-notification new-section tw-hidden tw-text-end md:tw-block">
              <div className="text-small text-green heavy">
                <FormattedMessage
                  id="hosting.active_listings_panel.new_text"
                  defaultMessage="New"
                />
              </div>
              <Link to={{ pathname: `/mylistings/${_id}/photos` }}>
                <div className="heavy underline">
                  <FormattedMessage
                    id="uploader.video_modal.title"
                    defaultMessage="Add videos"
                  />
                </div>
              </Link>
            </div>
          )}
          <div className="button-section tw-flex tw-basis-full tw-flex-wrap tw-gap-y-8 md:tw-basis-auto md:tw-flex-nowrap">
            {(status === "PAUSED" || status === "PAUSED_INTERNALLY") &&
              isEnabled && (
                <div className="status-action tw-basis-full md:tw-mr-8 md:tw-basis-auto">
                  <a
                    className="btn btn-block btn-default btn-outlined-light"
                    data-testing-id={`resume-listing-${_id}`}
                    href={`/mylistings/${_id}?openModal=true`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FormattedMessage
                      id="pause.modal.resume"
                      defaultMessage="Resume listing"
                    />
                  </a>
                </div>
              )}
            {/* Product request Removing Request reactivation button */}
            {/* { status === 'DRAFT' && isEnabled &&
              <div className="margin-md-right">
                <a
                  className="btn btn-block btn-default btn-outlined-light"
                  data-testing-id={`onboarding-space-${parentSpaceId}`}
                  href={`https://hosts.peerspace.com/onboarding?space=${parentSpaceId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage id="pause.button.request_reactivation" defaultMessage="Request reactivation" />
                </a>
              </div>
            } */}
            {status === "PENDING" && isEnabled && (
              <div className="status-action tw-basis-full md:tw-mr-8 md:tw-basis-auto">
                <a
                  className="btn btn-block btn-primary"
                  data-testing-id={`onboarding-space-${parentSpaceId}`}
                  href={`/host/space-hub/${parentSpaceId}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage
                    id="pause.button.required_updates"
                    defaultMessage="View required updates"
                  />
                </a>
              </div>
            )}
            <div className="margin-md-right edit-button tw-flex-1 md:tw-flex-none">
              <Link
                className="btn btn-block btn-default btn-outlined-light"
                to={{ pathname: `/mylistings/${_id}` }}
                data-testing-id={`edit-listing-${_id}`}
              >
                {shouldSetupFaq ? (
                  <FormattedMessage
                    id="hosting.active_listings_panel.set_up_faq_short_text"
                    defaultMessage="Set up FAQ"
                  />
                ) : (
                  <FormattedMessage
                    id="hosting.active_listings_panel.edit_link_text"
                    defaultMessage="Edit"
                  />
                )}
              </Link>
            </div>
            <div className="tw-flex-shrink-0">
              <button
                onClick={() => toggleModal("MyListingsShareModal", { listing })}
                className="btn btn-default btn-outlined-light"
                type="button"
                name="open share modal"
                data-testing-id="share-modal-button"
              >
                <img
                  className="ico"
                  src="/web-hosting/assets/images/share.svg?v=rb"
                  alt={intl.formatMessage({
                    id: "hosting.active_listings_panel.share_image_alt",
                    defaultMessage: "share",
                  })}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </IntlProviderHOC>
  );
};

ActiveListingsPanel.propTypes = {
  useType: PropTypes.string,
  panelClassname: PropTypes.string,
  modalId: PropTypes.string,
  toggleModal: PropTypes.func,
  workflow: PropTypes.object,
  intl: intlShape,
};

export default injectIntl(ActiveListingsPanel);
